import { useCallback, useState } from "react";
import { App } from "antd";
import {
  addDosificationWeek,
  getDosificationConfiguration,
  restoreEmmiDosification,
  setDosificationSlotCourses,
} from "../services/school-dosification/school-dosification.service";
import {
  DosificationConfiguration,
  SetSlotCoursesRequest,
} from "../services/school-dosification/school-dosification.types";

/**
 * SCRUM-804: estado de la dosificación personalizada de una escuela.
 *
 * Cada escritura recarga la configuración desde el backend en lugar de tocar
 * el estado local: el backend es el que decide qué ve la escuela en cada
 * semana (Emmi, un curso puesto a mano, o vacía), y duplicar esa resolución
 * acá sería la forma más fácil de que la pantalla mienta.
 */
export const useSchoolDosification = (schoolId: number) => {
  // De App.useApp(): el notification estático no se renderiza con React 19.
  const { notification } = App.useApp();
  const [configuration, setConfiguration] =
    useState<DosificationConfiguration | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const showError = useCallback((error: unknown, fallback: string) => {
    const message =
      (error as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message ?? fallback;

    notification.error({
      message: "No se pudo guardar",
      description: Array.isArray(message) ? message.join(", ") : message,
    });
  }, [notification]);

  const loadConfiguration = useCallback(
    async (programId: number, trimester: number) => {
      setLoading(true);
      try {
        setConfiguration(
          await getDosificationConfiguration(schoolId, programId, trimester)
        );
      } catch (error) {
        setConfiguration(null);
        showError(error, "No se pudo cargar la dosificación de esta escuela.");
      } finally {
        setLoading(false);
      }
    },
    [schoolId, showError]
  );

  /**
   * SCRUM-1076: every week edit (add, change, remove, reorder) sends the whole
   * list of the week. The backend validates it (duplicates, SCRUM-817, special
   * weeks) and the reload shows whatever it decided. `fallbackMessage` names
   * the action for an error the backend did not explain (network, 5xx).
   */
  const saveSlotCourses = useCallback(
    async (
      programId: number,
      data: SetSlotCoursesRequest,
      fallbackMessage = "No se pudieron guardar los cursos de esa semana."
    ) => {
      setSaving(true);
      try {
        await setDosificationSlotCourses(schoolId, programId, data);
        await loadConfiguration(programId, data.trimester);
        return true;
      } catch (error) {
        showError(error, fallbackMessage);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [schoolId, loadConfiguration, showError]
  );

  const addWeek = useCallback(
    async (programId: number, trimester: number) => {
      setSaving(true);
      try {
        await addDosificationWeek(schoolId, programId);
        await loadConfiguration(programId, trimester);
        return true;
      } catch (error) {
        showError(error, "No se pudo añadir la semana.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [schoolId, loadConfiguration, showError]
  );

  const restoreEmmi = useCallback(
    async (programId: number, trimester: number) => {
      setSaving(true);
      try {
        await restoreEmmiDosification(schoolId, programId);
        await loadConfiguration(programId, trimester);
        notification.success({
          message: "Dosificación Emmi restaurada",
          description:
            "Esta asignatura vuelve a usar la secuencia de Emmi en los tres trimestres.",
        });
        return true;
      } catch (error) {
        showError(error, "No se pudo restaurar la dosificación de Emmi.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [schoolId, loadConfiguration, showError, notification]
  );

  return {
    configuration,
    loading,
    saving,
    loadConfiguration,
    saveSlotCourses,
    addWeek,
    restoreEmmi,
  };
};
